import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from './Header'; // adjust path if Header is elsewhere
import styles from './AncientStyleCouplet.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

function AncientStyleCouplet() {
  // Tongsheng-like UI states
  const [isFiveWordCouplet, setIsFiveWordCouplet] = useState(true);
  const [coupletUnits, setCoupletUnits] = useState(Array(10).fill(''));
  const [submittedCouplet, setSubmittedCouplet] = useState('');

  // Same behavior as Assess.js
  const [feedback, setFeedback] = useState('');
  const [pingZeFeedback, setPingZeFeedback] = useState('');
  const [modelAssessment, setModelAssessment] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(null);

  // Optional: placeholder typing effect like Tongsheng (safe UI-only)
  const [placeholderText, setPlaceholderText] = useState('');
  const placeholderContent = 'Type here...';

  useEffect(() => {
    if (!coupletUnits[0]) {
      let index = 0;
      const interval = setInterval(() => {
        setPlaceholderText(placeholderContent.substring(0, index));
        index = (index + 1) % (placeholderContent.length + 1);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [coupletUnits]);

  const handleToggle = () => {
    setIsFiveWordCouplet((prev) => !prev);
    // 5-word couplet => 10 boxes, 7-word couplet => 14 boxes
    setCoupletUnits(Array(!isFiveWordCouplet ? 10 : 14).fill(''));
  };

  const handleInputChange = (index, value) => {
    const next = [...coupletUnits];
    next[index] = value;
    setCoupletUnits(next);
  };

  const buildCoupletText = () => {
    // If textarea has content, use it directly (same idea as Tongsheng)
    if (submittedCouplet.trim()) return submittedCouplet.trim();

    const wordsPerLine = isFiveWordCouplet ? 5 : 7;
    const line1 = coupletUnits.slice(0, wordsPerLine).join(' ').trim();
    const line2 = coupletUnits.slice(wordsPerLine).join(' ').trim();

    return `${line1}\n${line2}`.trim();
  };

  const handleSubmit = async () => {
    const coupletText = buildCoupletText();

    // Match Assess.js “complete all steps” behavior, but here we only need couplet text
    if (!coupletText) {
      setError('Please enter your couplet before submitting.');
      return;
    }

    // Reset outputs like Assess.js
    setLoading(true);
    setFeedback('');
    setPingZeFeedback('');
    setModelAssessment('');
    setError(null);
    setIsValid(null);

    // IMPORTANT: Keep backend logic exactly like previous for this type:
    // submissionType=couplet, regulationType=unregulated => level = tongsheng
    const endpoint = '/assess-couplet';
    const payload = {
      couplet: coupletText,
      user_level: 'guest',
      level: 'tongsheng',
    };

    try {
      const response = await axios.post(`${API_URL}${endpoint}`, payload, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });

      setIsValid(response.data.is_valid);
      setFeedback(response.data.feedback);
      setPingZeFeedback(response.data.ping_ze_feedback || '');
      setModelAssessment(response.data.model_assessment || '');
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred while processing your submission.');
    }

    setLoading(false);
  };

  const handleClear = () => {
    setSubmittedCouplet('');
    setIsFiveWordCouplet(true);
    setCoupletUnits(Array(10).fill(''));
    setFeedback('');
    setPingZeFeedback('');
    setModelAssessment('');
    setError(null);
    setLoading(false);
    setIsValid(null);
  };

  const renderCoupletInputs = () => {
    const wordsPerLine = isFiveWordCouplet ? 5 : 7;

    const line1 = [];
    for (let i = 0; i < wordsPerLine; i++) {
      line1.push(
        <input
          key={i}
          type="text"
          value={coupletUnits[i]}
          onChange={(e) => handleInputChange(i, e.target.value)}
          className={styles.unitBox}
          placeholder={i === 0 ? placeholderText : ''}
          onFocus={() => setPlaceholderText('')}
        />
      );

      // Tongsheng-like spacing “gaps”
      if (isFiveWordCouplet && i === 1) {
        line1.push(<div key={`gap-${i}`} className={styles.gap} />);
      } else if (!isFiveWordCouplet && (i === 1 || i === 3)) {
        line1.push(<div key={`gap-${i}`} className={styles.gap} />);
      }
    }

    const line2 = [];
    for (let i = wordsPerLine; i < (isFiveWordCouplet ? 10 : 14); i++) {
      line2.push(
        <input
          key={i}
          type="text"
          value={coupletUnits[i]}
          onChange={(e) => handleInputChange(i, e.target.value)}
          className={styles.unitBox}
        />
      );

      if (isFiveWordCouplet && i === 6) {
        line2.push(<div key={`gap-${i}`} className={styles.gap} />);
      } else if (!isFiveWordCouplet && (i === 8 || i === 10)) {
        line2.push(<div key={`gap-${i}`} className={styles.gap} />);
      }
    }

    return (
      <>
        <div className={styles.line}>{line1}</div>
        <div className={styles.line}>{line2}</div>
      </>
    );
  };

  return (
    <div>
      <Header />
      <div className={styles.pageOffset}>

        <div className={styles.container}>
          <div className={styles.submissionContainer}>
            <h2>Ancient Style Couplet</h2>

            {/* Toggle like Tongsheng */}
            <div className={styles.toggleContainer}>
              <span>{isFiveWordCouplet ? '5-word couplet' : '7-word couplet'}</span>
              <label className={styles.switch}>
                <input type="checkbox" checked={!isFiveWordCouplet} onChange={handleToggle} />
                <span className={`${styles.slider} ${styles.round}`} />
              </label>
            </div>

            <p>Type in unit by unit:</p>
            {renderCoupletInputs()}

            <p>OR</p>

            {/* Keep punctuation warning like Assess */}
            <textarea
              placeholder={`Enter your couplet here...\n\nNote: Please remove all punctuation from your couplet including apostrophes!`}
              value={submittedCouplet}
              onChange={(e) => setSubmittedCouplet(e.target.value)}
              className={styles.textArea}
              rows={6}
            />

            <div className={styles.submitButtonsContainer}>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`${styles.aiButton} ${styles.button}`}
                title="Submit for assessment"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>

              <button
                onClick={handleClear}
                disabled={loading}
                className={`${styles.button} ${styles.resetButton}`}
              >
                Clear
              </button>
            </div>

            {loading && <div className={styles.loader} />}

            {error && <div className={styles.errorMessage}>{error}</div>}

            {/* Feedback areas - SAME behavior as Assess.js, just shown in this UI */}
            {feedback && (
              <div className={`${styles.assessmentResult} ${isValid === false ? styles.invalidFeedback : ''}`}>
                <h3>{isValid === false ? 'Invalid Submission' : 'Feedback'}</h3>
                <p>{feedback}</p>
              </div>
            )}

            {modelAssessment && isValid && (
              <div className={styles.assessmentResult}>
                <h3>Model Assessment</h3>
                <p className={styles.generatedText}>{modelAssessment}</p>
              </div>
            )}

            {pingZeFeedback && (
              <div className={styles.pingZeFeedback}>
                <h3>Ping/Ze Feedback</h3>
                {pingZeFeedback.split('\n').map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AncientStyleCouplet;
